import { z } from 'zod';
import { IsoDateTime, PositiveCents } from './primitives.js';
import { OpaqueRef } from './ids.js';
/**
 * A bookable interval, **half-open `[start, end)`** (agreement section 4.2,
 * rule 3). Half-open is not a detail: it is why a 13:00–14:00 booking and a
 * 14:00–15:00 booking do not collide, and it is what the Postgres `tstzrange`
 * on `appointments` stores.
 */
export const Slot = z.object({
    start: IsoDateTime,
    end: IsoDateTime,
    /**
     * The **primary** lane — the person, when the service needs one (spec
     * §4.1.2). Every caller that predates resource roles reads and writes exactly
     * this field, and for a service that needs one lane it is the only one there
     * is.
     */
    resource_id: OpaqueRef,
    /**
     * **The other lanes this slot occupies** (v1.14, additive; spec §4.1.2).
     *
     * A massage that needs a therapist *and* a treatment room is one slot on two
     * lanes: `resource_id` is the therapist and this carries the room. It is on
     * `Slot` rather than only on `OfferedSlot` because the same lanes ride the
     * hold, the booking and the reschedule: a caller books the combination it was
     * offered by echoing the slot back, which is what every caller already does.
     *
     * **Absent means one of two things, and both are correct.** On an *offered*
     * slot it means the service needs one lane, which is the whole fleet today.
     * On a *request* it means "the server chooses": a caller that has never
     * learned the word sends `resource_id` alone and the provider picks a free
     * companion lane for each other role inside the booking's own transaction —
     * the offered start was free on some combination, and if it no longer is,
     * that is the ordinary `409`. That rule is what keeps the change additive.
     *
     * Never the primary lane again, never two lanes of one role; the store
     * refuses either with `400 invalid`. Bounded by §5.5.5's 25 resources.
     */
    also_resource_ids: z.array(OpaqueRef).max(24).optional(),
});
/**
 * **Which role a resource plays for one item** (spec §4.1.2, migration 0056).
 *
 * `item_resources.role`. `'any'` is the set as it has always been read — *any
 * one of these delivers it* — and it is the default on every row, which is
 * what makes roles a data change rather than a redesign: an item whose links
 * are all `'any'` walks exactly the union the engine has walked since MVP 1.
 * An item with a non-empty `'person'` side **and** a non-empty `'room'` side
 * needs *one of each*, and the engine walks their cross product.
 *
 * Three values and not a free string, because the engine partitions by them
 * and a fourth role would be a fourth side of a product nobody has designed.
 */
export const ResourceRole = z.enum(['any', 'person', 'room']);
/** A slot as offered to a caller: which service it is for, and how many fit. */
export const OfferedSlot = Slot.extend({
    service_id: OpaqueRef,
    capacity: z.number().int().min(1),
    /**
     * **Additive, and present only on a pool** (v1.8 §3).
     *
     * **How many parties may be booked at this start** — lanes, not seats, and
     * measured across the slot's whole occupied span, because a booking holds a
     * lane for all of it. A restaurant's twelve interchangeable tables with
     * nothing on the book answer `12`; a 16:45 seating that runs into an evening
     * where eleven of them are already held answers `1`, which is the true and
     * useful number even though twelve tables are free at 16:45 itself.
     *
     * **Absent means the resource has one lane** — not "one lane is free". That
     * distinction is the field's other job: it is how a caller, and the
     * conformance suite, tells a pool from a single room, and therefore which
     * form of the agreement's rule 3 applies. Where it is absent the old sentence
     * holds unchanged (offered slots never overlap on that resource); where it is
     * present they may overlap, bounded by lanes the caller cannot see.
     *
     * It is deliberately **not** a bound the caller is expected to verify
     * arithmetically. `units_free` is a minimum over a span, so it cannot be
     * turned back into the lanes free at one instant, and v1.8 §3 says so rather
     * than implying a check that does not hold. The guarantee a caller relies on
     * is the one the provider makes and the suite exercises by booking: **any
     * subset of the offered slots can be booked together.**
     *
     * Deliberately *not* folded into `capacity`, which answers the other
     * question — how large a party one booking here may carry — and which a
     * consumer already reads that way. Two facts, two fields; see v1.8 §3 for the
     * options that were weighed and why a live field's meaning was not moved.
     */
    units_free: z.number().int().min(1).optional(),
    /**
     * **Additive, and present only on a stay** (v1.1 §2.6, spec §4.7 addition 2).
     *
     * The amendment settles it in one line: "the offered slot spans the whole stay
     * and carries an additive `value_cents` (server-priced: nightly rates + fees +
     * tax)". A grid slot does not carry one and must not start to — a spa's price
     * is `GET /services`' `price_cents` and duplicating it per slot would be a
     * second place for it to be wrong — so this is `.optional()` rather than
     * defaulted, and every caller that predates lodging sees exactly the bytes it
     * saw before.
     *
     * It exists because "an agent quoting a stay without a price is not quoting
     * anything" (§4.7): a stay's price cannot be looked up from the catalog, since
     * it is a sum over the calendar for the dates that were asked about.
     */
    value_cents: PositiveCents.optional(),
});
/** `live` only when this system holds the book for the tenant (spec 4.4). */
export const Authority = z.enum(['live', 'approximate']);
/**
 * `slots` is what matches the ask. `alternatives` is what to offer when it does
 * not — and it is populated whenever the ask does not fit, never left empty
 * because the answer was "no". The agreement calls this out as mattering more
 * than the rest of the document (section 4.2, rule 1).
 */
export const AvailabilityResult = z.object({
    authority: Authority,
    slots: z.array(OfferedSlot),
    alternatives: z.array(OfferedSlot),
    summary: z.string(),
});
export const AvailabilityQuery = z.object({
    service_id: OpaqueRef.optional(),
    date_from: z.string(),
    date_to: z.string(),
    party_size: z.number().int().min(1).max(50).default(1),
    timezone: z.string().optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=slot.js.map